import React, { useMemo, useState } from 'react';
import { Toaster, toast } from 'sonner';
import { Condition, ConditionStatus, ConditionSource } from '../types';
import { mockConditions } from '../data/mockConditions';
import { ConditionEditCard } from '../components/ConditionEditCard';
import { PreviewCardChangesModal } from '../components/PreviewCardChangesModal';
import { BulkEditToolbar } from '../components/BulkEditToolbar';
import { PreviewChangesModal } from '../components/PreviewChangesModal';
import { MultiSelectDropdown } from '../components/MultiSelectDropdown';
import { motion, AnimatePresence } from 'framer-motion';
import { Edit3, CheckCircle2, XCircle, Search, CircleDot, Tag, User, SearchX, Eye, ArrowLeft } from 'lucide-react';
export function ConditionsPage() {
  const [conditions, setConditions] = useState<Condition[]>(mockConditions);
  const [pendingCardEdits, setPendingCardEdits] = useState<Record<string, Partial<Condition>>>({});
  const [isPreviewCardModalOpen, setIsPreviewCardModalOpen] = useState(false);
  // Multi-select state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isPreviewBulkModalOpen, setIsPreviewBulkModalOpen] = useState(false);
  const [bulkUpdates, setBulkUpdates] = useState<any>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<Set<ConditionStatus>>(new Set());
  const [typeFilter, setTypeFilter] = useState<Set<ConditionSource>>(new Set());
  const [assigneeFilter, setAssigneeFilter] = useState<Set<string>>(new Set());
  const uniqueAssignees = useMemo(() => {
    const assignees = new Set(conditions.map(c => c.assignedTo));
    return Array.from(assignees).sort();
  }, [conditions]);
  const filteredConditions = useMemo(() => {
    return conditions.filter(c => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!c.name.toLowerCase().includes(q) && !c.description.toLowerCase().includes(q)) {
          return false;
        }
      }
      if (statusFilter.size > 0 && !statusFilter.has(c.status)) return false;
      if (typeFilter.size > 0 && !typeFilter.has(c.source)) return false;
      if (assigneeFilter.size > 0 && !assigneeFilter.has(c.assignedTo)) return false;
      return true;
    });
  }, [conditions, searchQuery, statusFilter, typeFilter, assigneeFilter]);
  const handleToggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };
  const handleSelectAll = () => {
    if (selectedIds.size === filteredConditions.length) {
      setSelectedIds(newSelected => {
        const next = new Set(newSelected);
        filteredConditions.forEach(c => next.delete(c.id));
        return next;
      });
    } else {
      setSelectedIds(newSelected => {
        const next = new Set(newSelected);
        filteredConditions.forEach(c => next.add(c.id));
        return next;
      });
    }
  };
  const handleBulkPreview = (updates: any) => {
    setBulkUpdates(updates);
    setIsPreviewBulkModalOpen(true);
  };
  const handleBulkApply = (updates: any) => {
    const updatesToApply = Object.keys(updates).length > 0 ? updates : bulkUpdates;
    setConditions(prev => prev.map(c => {
      if (selectedIds.has(c.id)) {
        return {
          ...c,
          ...updatesToApply
        };
      }
      return c;
    }));
    toast.success(`Successfully updated ${selectedIds.size} conditions`);
    setSelectedIds(new Set());
    setBulkUpdates({});
    setIsPreviewBulkModalOpen(false);
  };
  const handleCardEditChange = (id: string, field: keyof Condition, value: any) => {
    setPendingCardEdits(prev => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        [field]: value
      }
    }));
  };
  const handleSaveCardEdits = () => {
    setConditions(prev => prev.map(c => {
      if (pendingCardEdits[c.id]) {
        return {
          ...c,
          ...pendingCardEdits[c.id]
        };
      }
      return c;
    }));
    const changedCount = Object.keys(pendingCardEdits).filter(id => Object.keys(pendingCardEdits[id]).length > 0).length;
    if (changedCount > 0) {
      toast.success(`Successfully updated ${changedCount} conditions`);
    }
    setPendingCardEdits({});
    setIsPreviewCardModalOpen(false);
  };
  const handleDiscardAll = () => {
    setPendingCardEdits({});
    toast('All changes discarded');
  };
  const totalCardChanges = Object.values(pendingCardEdits).reduce((acc, edits) => acc + Object.keys(edits).length, 0);
  const changedConditionCount = Object.keys(pendingCardEdits).filter(id => Object.keys(pendingCardEdits[id]).length > 0).length;
  const activeFilterCount = (statusFilter.size > 0 ? 1 : 0) + (typeFilter.size > 0 ? 1 : 0) + (assigneeFilter.size > 0 ? 1 : 0);
  const allSelected = filteredConditions.length > 0 && selectedIds.size === filteredConditions.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < filteredConditions.length;
  return <div className="min-h-screen bg-gray-50 pb-24" data-id="element-247">
      <Toaster position="top-right" data-id="element-248" />

      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30" data-id="element-249">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-250">
          <div className="flex justify-between items-center h-14" data-id="element-251">
            <div className="flex items-center" data-id="element-252">
              <button onClick={() => window.history.back()} className="flex items-center text-xs font-medium text-gray-500 hover:text-[#1C1C3A] mr-4 px-2.5 py-1.5 rounded-md border border-gray-300 hover:border-gray-400 bg-white hover:bg-gray-50 transition-colors" data-id="element-253">
                <ArrowLeft className="w-3.5 h-3.5 mr-1.5" data-id="element-254" />
                Return to Conditions
              </button>
              <div className="w-px h-6 bg-gray-200 mr-4" data-id="element-255" />
              <input type="checkbox" checked={allSelected} ref={input => {
              if (input) input.indeterminate = someSelected;
            }} onChange={handleSelectAll} className="h-4 w-4 text-[#D44444] focus:ring-[#D44444] border-gray-300 rounded cursor-pointer mr-3" title="Select All" data-id="element-256" />
              <Edit3 className="w-4 h-4 mr-2 text-[#1C1C3A]" data-id="element-257" />
              <h1 className="text-lg font-semibold text-gray-900" data-id="element-258">
                Edit Conditions
              </h1>
              <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-259">
                {filteredConditions.length}
              </span>
            </div>
            <div className="flex items-center gap-2" data-id="element-260">
              <MultiSelectDropdown className="w-32" options={[{
              label: 'Need',
              value: 'Need'
            }, {
              label: 'Requested',
              value: 'Requested'
            }, {
              label: 'Received',
              value: 'Received'
            }, {
              label: 'Review',
              value: 'Review'
            }, {
              label: 'Cleared',
              value: 'Cleared'
            }, {
              label: 'Waived',
              value: 'Waived'
            }, {
              label: 'N/A',
              value: 'N/A'
            }]} selectedValues={statusFilter} onChange={setStatusFilter} placeholder="All Statuses" icon={<CircleDot className="h-3.5 w-3.5" data-id="element-262" />} data-id="element-261" />
              <MultiSelectDropdown className="w-32" options={[{
              label: 'Underwriter',
              value: 'Underwriter'
            }, {
              label: 'Processor',
              value: 'Processor'
            }, {
              label: 'Loan Officer',
              value: 'Loan Officer'
            }, {
              label: 'Compliance',
              value: 'Compliance'
            }]} selectedValues={typeFilter} onChange={setTypeFilter} placeholder="All Types" icon={<Tag className="h-3.5 w-3.5" data-id="element-264" />} data-id="element-263" />
              <MultiSelectDropdown className="w-36" options={uniqueAssignees.map(a => ({
              label: a,
              value: a
            }))} selectedValues={assigneeFilter} onChange={setAssigneeFilter} placeholder="All Assignees" icon={<User className="h-3.5 w-3.5" data-id="element-266" />} data-id="element-265" />
              {activeFilterCount > 0 && <button onClick={() => {
              setStatusFilter(new Set());
              setTypeFilter(new Set());
              setAssigneeFilter(new Set());
            }} className="text-xs text-[#D44444] hover:text-[#B83333] font-medium px-1" data-id="element-267">
                  Clear
                </button>}
              <div className="relative w-52 ml-1" data-id="element-268">
                <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none" data-id="element-269">
                  <Search className="h-3.5 w-3.5 text-gray-400" data-id="element-270" />
                </div>
                <input type="text" placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="block w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#D44444] focus:border-[#D44444] text-xs" data-id="element-271" />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Edit Cards */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5" data-id="element-272">
        <div className="space-y-3" data-id="element-273">
          {filteredConditions.map(condition => <ConditionEditCard key={condition.id} condition={condition} edits={pendingCardEdits[condition.id] || {}} onChange={handleCardEditChange} uniqueAssignees={uniqueAssignees} isSelected={selectedIds.has(condition.id)} onToggleSelect={handleToggleSelect} data-id="element-274" />)}
          {filteredConditions.length === 0 && <div className="text-center py-12 flex flex-col items-center justify-center text-gray-500" data-id="element-275">
              <SearchX className="w-8 h-8 text-gray-300 mb-3" data-id="element-276" />
              <p className="text-sm" data-id="element-277">
                No conditions found matching your search.
              </p>
            </div>}
        </div>
      </main>

      {/* Bulk Edit Toolbar */}
      <BulkEditToolbar selectedCount={selectedIds.size} onClearSelection={() => setSelectedIds(new Set())} onPreview={handleBulkPreview} onApply={handleBulkApply} uniqueAssignees={uniqueAssignees} data-id="element-278" />

      {/* Sticky Bottom Save Bar (only show if no bulk selection) */}
      <AnimatePresence data-id="element-279">
        {totalCardChanges > 0 && selectedIds.size === 0 && <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]" data-id="element-280">
            <div className="flex items-center justify-between px-6 py-3 max-w-7xl mx-auto" data-id="element-281">
              <div className="flex items-center space-x-3" data-id="element-282">
                <span className="flex items-center justify-center w-5 h-5 bg-[#D44444] rounded-full text-[10px] font-bold text-white" data-id="element-283">
                  {changedConditionCount}
                </span>
                <span className="text-sm font-medium text-gray-900" data-id="element-284">
                  {changedConditionCount} condition
                  {changedConditionCount !== 1 ? 's' : ''} modified
                </span>
                <span className="text-xs text-gray-500" data-id="element-285">
                  ({totalCardChanges} field{totalCardChanges !== 1 ? 's' : ''})
                </span>
              </div>

              <div className="flex items-center space-x-2" data-id="element-286">
                <button onClick={handleDiscardAll} className="flex items-center px-3 py-1.5 rounded-md text-xs font-medium text-gray-600 bg-white border border-gray-300 hover:bg-gray-50 transition-colors" data-id="element-287">
                  <XCircle className="w-3.5 h-3.5 mr-1.5 text-gray-400" data-id="element-288" />
                  Discard
                </button>
                <button onClick={() => setIsPreviewCardModalOpen(true)} className="flex items-center px-3 py-1.5 rounded-md text-xs font-medium bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors" data-id="element-289">
                  <Eye className="w-3.5 h-3.5 mr-1.5 text-gray-400" data-id="element-290" />
                  Preview
                </button>
                <button onClick={handleSaveCardEdits} className="flex items-center px-4 py-1.5 rounded-md text-xs font-medium bg-[#D44444] text-white hover:bg-[#B83333] shadow-sm transition-colors" data-id="element-291">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" data-id="element-292" />
                  Save All
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Preview Modal */}
      <PreviewCardChangesModal isOpen={isPreviewCardModalOpen} onClose={() => setIsPreviewCardModalOpen(false)} onConfirm={handleSaveCardEdits} conditions={filteredConditions} pendingEdits={pendingCardEdits} data-id="element-293" />

      {/* Bulk Preview Modal */}
      <PreviewChangesModal isOpen={isPreviewBulkModalOpen} onClose={() => setIsPreviewBulkModalOpen(false)} onConfirm={() => handleBulkApply({})} selectedConditions={conditions.filter(c => selectedIds.has(c.id))} updates={bulkUpdates} data-id="element-294" />
    </div>;
}